import React, { useState } from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import { Alert, Box, Container, Tab, Tabs } from '@mui/material';

import { expandTabRoutes, preRegisterSpotlightRoutes } from '~/components/Common/Spotlight/SpotlightProvider';

import useCurrentUser from '~/hooks/useCurrentUser';

import { hasFullAccess, hasReadAccess } from '~/utils/lere';

import PageHeader from '../Common/Elements/PageHeader';

import ClusterMap from './ClusterMap';
import LereTPFMEmailTrigger from './LereTPFMEmailTrigger';
import LereUserActivity from './LereUserActivity';
import RMChangeLog from './RMChangeLog';
import RMExclusionOverrides from './RMExclusionOverrides';
import RMSurchargeSuggestions from './RMSurchargeSuggestions';
import RMToursPricingChangeLog from './RMToursPricingChangeLog';
import RMToursPricingSuggestions from './RMToursPricingSuggestions';

export const TabsMap = {
  'rm-surcharge-suggestions': {
    label: '[RM] Surcharge Suggestions',
    slug: 'rm-surcharge-suggestions',
    component: RMSurchargeSuggestions,
  },
  'rm-change-log': {
    label: '[RM] Hotels Change Log',
    slug: 'rm-change-log',
    component: RMChangeLog,
  },
  'rm-exclusion-overrides': {
    label: '[RM] Exclusions',
    slug: 'rm-exclusion-overrides',
    component: RMExclusionOverrides,
  },
  'rm-tours-pricing-suggestions': {
    label: '[RM] Tours Pricing Suggestions',
    slug: 'rm-tours-pricing-suggestions',
    component: RMToursPricingSuggestions,
  },
  'rm-tours-pricing-history': {
    label: '[RM] Tours Change Log',
    slug: 'rm-tours-pricing-history',
    component: RMToursPricingChangeLog,
  },
  'cluster-map': {
    label: 'Cluster Map',
    slug: 'cluster-map',
    component: ClusterMap,
  },
  'email-trigger': {
    label: 'Email Trigger',
    slug: 'email-trigger',
    component: LereTPFMEmailTrigger,
  },
  'user-activity': {
    label: 'User Activity',
    slug: 'user-activity',
    component: LereUserActivity,
  },
};
type TabKey = keyof typeof TabsMap;
const allTabs = Object.keys(TabsMap);

preRegisterSpotlightRoutes(expandTabRoutes('/lere', 'LERE', TabsMap));

export default function LereAdminPageContainer() {
  const { section = 'rm-surcharge-suggestions' } = useParams<{ section?: TabKey }>();
  const { user } = useCurrentUser();
  const currentTab = TabsMap[section];
  const title = `LERE / ${currentTab.label}`;
  const [lereError, setLereError] = useState<Error | undefined>(undefined);

  if (!hasFullAccess(user) && !hasReadAccess(user)) {
    return (
      <Container maxWidth="xl" sx={{ backgroundColor: 'background.default' }}>
        <Alert severity="error" sx={{ mb: 2, maxWidth: 480, width: '100%' }}>
          You do not have the correct permissions to access this page.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ backgroundColor: 'background.default' }}>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <PageHeader title={title} />

      {lereError && (
        <Alert severity="error" onClose={() => setLereError(undefined)}>
          {lereError.message ?? lereError.toString()}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={section} variant="scrollable" scrollButtons="auto">
          {allTabs.map((tab) => (
            <Tab
              key={tab}
              value={tab}
              label={TabsMap[tab].label}
              to={`/lere/${tab}`}
              component={Link}
              sx={{
                minWidth: 30,
                padding: '12px 10px',
              }}
            />
          ))}
        </Tabs>
      </Box>

      <Box mt={4}>
        <Box>
          <currentTab.component onLereError={setLereError} />
        </Box>
      </Box>
    </Container>
  );
}
